// Tour steps configuration for first-time user onboarding
// Uses react-joyride to create an interactive product tour

import {
  Person,
  Chat,
  Settings,
  CreditCard,
  CardMembership,
  Assignment,
  People,
  FitnessCenter,
  Schedule,
  CalendarToday,
} from "@mui/icons-material";

export const tourSteps = [
  {
    target: ".dashboard-header",
    content: "Welcome to Nativus Coach! Let me show you around your new dashboard.",
    placement: "bottom",
    disableBeacon: true,
  },
  {
    target: ".clients-section",
    content: "This is where you'll manage all your clients. Add your first client to get started!",
    placement: "top",
  },
  {
    target: ".programs-section",
    content: "Create and manage training programs for your clients here.",
    placement: "top",
  },
  {
    target: ".calendar-section",
    content: "Schedule sessions and manage your bookings with the calendar.",
    placement: "top",
  },
  {
    target: ".market-hub-section",
    content: "Access and share programs, assessments, and exercises in the Market Hub.",
    placement: "top",
  },
  {
    target: ".settings-menu",
    content: "Customize your account, manage subscriptions, and configure payment settings here.",
    placement: "left",
  },
  {
    target: ".onboarding-checklist",
    content: "Complete these tasks to get the most out of Nativus Coach. We'll guide you every step of the way!",
    placement: "left",
  },
];

// Checklist items configuration
export const checklistItems = [
  {
    key: "complete_profile",
    title: "Complete Your Profile",
    description: "Add your photo, bio, and contact information",
    icon: <Person />,
    route: "/admin/settings",
    help: {
      text: "Complete your profile to help clients get to know you better. Add a professional photo and ensure your contact information is accurate.",
      videoUrl: null, // e.g., "https://www.youtube.com/embed/xxxxx"
      linkUrl: null, // e.g., "https://docs.nativus.com/profile-setup"
      linkText: null, // e.g., "View Documentation"
    },
    guidance: [
      {
        target: '[data-tour="profile-section"]',
        message: "Start here! Add your profile photo and personal information to help clients connect with you.",
      },
    ],
  },
  {
    key: "setup_communication",
    title: "Configure Communication Settings",
    description: "Configure timezone and notifications",
    icon: <Chat />,
    route: "/admin/settings",
    help: {
      text: "Set your timezone to ensure all appointments are scheduled correctly. Configure your notification preferences to stay informed about important updates.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="communication-section"]',
        message: "Configure your timezone and notification preferences here to stay in sync with your clients.",
      },
    ],
  },
  {
    key: "complete_setup",
    title: "Complete Initial Setup",
    description: "Configure your coaching preferences and defaults",
    icon: <Settings />,
    route: "/admin/settings",
    help: {
      text: "Configure your default coaching settings including measurement units, date formats, and session defaults. These settings will be applied across your account.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="setup-section"]',
        message: "Set up your coaching preferences including units, date formats, and session defaults.",
      },
    ],
  },
  {
    key: "setup_payment_connection",
    title: "Set Up Payment Connection",
    description: "Connect eWay to accept client payments",
    icon: <CreditCard />,
    route: "/admin/settings",
    help: {
      text: "Connect your eWay payment gateway to start accepting payments from clients. You'll need your eWay API credentials to complete this step.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="payment-section"]',
        message: "Connect your eWay payment gateway here. You'll need your API key and password from eWay.",
      },
    ],
  },
  {
    key: "setup_subscriptions",
    title: "Set Up Subscriptions",
    description: "Create subscription plans for your services",
    icon: <CardMembership />,
    route: "/admin/services",
    help: {
      text: "Create subscription plans to offer recurring services to your clients. Define pricing, billing frequency, and what's included in each plan.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="add-subscription-button"]',
        message:
          "Click the 'Add new' button to create your first subscription plan. Define pricing, billing frequency, and services included.",
      },
    ],
  },
  {
    key: "setup_landing_page",
    title: "Landing Page Setup",
    description: "Create your landing page to attract new clients",
    icon: <Assignment />,
    route: "/admin/landing-page",
    help: {
      text: "Set up your landing page to showcase your services and attract new clients. This is where potential clients will learn about you and sign up for your services.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="landing-page-setup"]',
        message:
          "Configure your landing page here. Add your branding, services, and customize the layout to attract new clients.",
      },
    ],
  },
  {
    key: "setup_forms",
    title: "Set Up Forms",
    description: "Configure intake forms and questionnaires",
    icon: <Assignment />,
    route: "/admin/forms",
    help: {
      text: "Create custom forms to collect information from clients. Use onboarding forms for new clients, feedback forms after sessions, and check-in forms for regular updates.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="add-form-button"]',
        message:
          "Click 'Add new' and select a form type to create your first form. You can choose from Onboarding, Feedback, Landing Page, or Check In forms.",
      },
    ],
  },
  {
    key: "add_first_client",
    title: "Add Your First Client",
    description: "Start building your client base",
    icon: <People />,
    route: "/clients",
    help: {
      text: "Add your first client to get started. You can enter their email and let the automated onboarding journey do the rest!",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="add-client-button"]',
        message:
          "Click the 'Add Client' button to create your first client. Fill in their basic information to get started.",
      },
    ],
  },
  {
    key: "create_first_program",
    title: "Create Your First Program",
    description: "Design a training program for your clients",
    icon: <FitnessCenter />,
    route: "/dynamic-builder",
    help: {
      text: "Use the Dynamic Builder to create a training program. Add exercises, set durations, and organise sessions into a structured program for your clients.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="program-name"]',
        message:
          "Start by giving your program a name and description. Then add exercises to build out your training program.",
      },
    ],
  },
  {
    key: "setup_availability",
    title: "Set Up Availability",
    description: "Configure your working hours and availability",
    icon: <Schedule />,
    route: "/admin/users",
    help: {
      text: "Set your working hours so clients know when you're available for sessions. Define your weekly schedule including any breaks or unavailable times.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="edit-user-button"]',
        message:
          "Click the edit button on your user profile, then navigate to the 'Availability' tab to set your working hours.",
      },
    ],
  },
  {
    key: "schedule_first_session",
    title: "Schedule Your First Session",
    description: "Book a session with a client",
    icon: <CalendarToday />,
    route: "/calendar",
    help: {
      text: "Schedule your first session with a client using the calendar. Select a date and time, choose the client, and assign a service or program.",
      videoUrl: null,
      linkUrl: null,
      linkText: null,
    },
    guidance: [
      {
        target: '[data-tour="create-booking-button"]',
        message: "Click 'Create new booking' and select 'Client Booking' to schedule your first session with a client.",
      },
    ],
  },
];

// Tour theme customization
export const tourStyles = {
  options: {
    arrowColor: "#fff",
    backgroundColor: "#fff",
    overlayColor: "rgba(0, 0, 0, 0.5)",
    primaryColor: "#1976d2",
    textColor: "#333",
    width: 380,
    zIndex: 10000,
  },
  buttonNext: {
    backgroundColor: "#1976d2",
    borderRadius: "8px",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "600",
    padding: "10px 20px",
  },
  buttonBack: {
    color: "#666",
    fontSize: "14px",
    marginRight: "10px",
  },
  buttonSkip: {
    color: "#999",
    fontSize: "14px",
  },
  tooltip: {
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
    padding: "20px",
  },
  tooltipContainer: {
    textAlign: "left",
  },
  tooltipTitle: {
    fontSize: "18px",
    fontWeight: "700",
    marginBottom: "10px",
  },
  tooltipContent: {
    fontSize: "14px",
    lineHeight: "1.6",
  },
};
